import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, dropdownAPI, growerInvoiceAPI, growersAPI } from '../services/api';

const Reports = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('grower');

  // Get today's date in local timezone (AEST)
  const getTodayLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set default dates to today (local timezone)
  const today = getTodayLocal();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  const [groupBy, setGroupBy] = useState('day');
  const [reportData, setReportData] = useState([]);
  const [growers, setGrowers] = useState([]);
  const [selectedGrower, setSelectedGrower] = useState('all');
  const [generatedGrower, setGeneratedGrower] = useState('all'); // Grower used for current report
  const [produceTypes, setProduceTypes] = useState([]);
  const [selectedProduceType, setSelectedProduceType] = useState('all');
  const [pivotGrowers, setPivotGrowers] = useState([]); // Growers from pivot report response

  // Grower Report invoice metadata (grower_size report only)
  const emptyInvoice = {
    id: null,
    invoice_number: '',
    grower_name: '',
    pickup_date: '',
    pack_date: '',
    bins: '',
    original_grower_invoice_number: '',
  };
  const [invoice, setInvoice] = useState(emptyInvoice);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceMessage, setInvoiceMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [extraEmail, setExtraEmail] = useState('');

  const reportTypes = [
    { value: 'customer', label: 'By Customer' },
    { value: 'grower', label: 'By Grower' },
    { value: 'grower_size', label: 'By Grower (for Grower Copy)' },
    { value: 'grower_pivot', label: 'Grower Pivot (Size/Colour vs Growers)' },
    { value: 'sheet_summary', label: 'Pallet Sheet Summary' },
    { value: 'date', label: 'By Date' },
    { value: 'produce', label: 'By Produce Type' },
  ];

  // Fetch growers and produce types list on component mount
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [growerData, produceData] = await Promise.all([
          dropdownAPI.getByType('grower'),
          dropdownAPI.getByType('produce_type')
        ]);
        setGrowers(growerData.values || []);
        setProduceTypes(produceData.values || []);
      } catch (err) {
        console.error('Failed to fetch dropdowns:', err);
      }
    };
    fetchDropdowns();
  }, []);

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    try {
      setLoading(true);
      setError('');
      let data;

      switch (reportType) {
        case 'customer':
          data = await reportsAPI.getByCustomer(startDate, endDate);
          break;
        case 'grower':
          data = await reportsAPI.getByGrower(startDate, endDate);
          break;
        case 'grower_size':
          data = await reportsAPI.getByGrowerSize(startDate, endDate, selectedGrower);
          setGeneratedGrower(selectedGrower); // Save the grower used for this report
          await loadOrPreviewInvoice(selectedGrower, startDate, endDate);
          break;
        case 'date':
          data = await reportsAPI.getByDate(startDate, endDate, groupBy);
          break;
        case 'grower_pivot':
          data = await reportsAPI.getGrowerPivot(startDate, endDate, selectedProduceType);
          setPivotGrowers(data.growers || []);
          break;
        case 'sheet_summary':
          data = await reportsAPI.getSheetSummary(startDate, endDate);
          break;
        case 'produce':
          data = await reportsAPI.getByProduce(startDate, endDate);
          break;
        default:
          data = { reports: [] };
      }

      setReportData(data.reports || []);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      setError('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Load an existing saved invoice for this grower/date range, or
  // show the next number that will be assigned if we save a new one.
  // 'all' growers means no invoice (invoices are per specific grower).
  const loadOrPreviewInvoice = async (grower, start, end) => {
    setInvoiceMessage('');
    if (!grower || grower === 'all') {
      setInvoice(emptyInvoice);
      return;
    }
    try {
      const existing = await growerInvoiceAPI.findByGrower(grower, start, end);
      if (existing.invoice) {
        setInvoice({
          id: existing.invoice.id,
          invoice_number: existing.invoice.invoice_number || '',
          grower_name: existing.invoice.grower_name || '',
          pickup_date: existing.invoice.pickup_date
            ? existing.invoice.pickup_date.slice(0, 10)
            : '',
          pack_date: existing.invoice.pack_date
            ? existing.invoice.pack_date.slice(0, 10)
            : '',
          bins: existing.invoice.bins ?? '',
          original_grower_invoice_number:
            existing.invoice.original_grower_invoice_number || '',
        });
      } else {
        const [next, growerLookup] = await Promise.all([
          growerInvoiceAPI.getNextNumber(),
          growersAPI.getByCode(grower).catch(() => ({ grower: null })),
        ]);
        setInvoice({
          ...emptyInvoice,
          invoice_number: next.invoice_number,
          grower_name: growerLookup.grower?.name || '',
        });
      }
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setInvoice(emptyInvoice);
    }
  };

  const totalQty = reportData.reduce(
    (sum, row) => sum + parseInt(row.total_qty || 0, 10),
    0
  );
  const binsNum = parseInt(invoice.bins, 10);
  const binAvg =
    binsNum > 0 ? (totalQty / binsNum).toFixed(2) : null;

  const handleInvoiceChange = (field, value) => {
    setInvoice((prev) => ({ ...prev, [field]: value }));
    setInvoiceMessage('');
  };

  const handleEmailInvoice = async () => {
    if (!invoice.id) {
      setEmailMessage('Save the invoice first before emailing.');
      return;
    }
    try {
      setEmailSending(true);
      setEmailMessage('');
      const extras = extraEmail
        .split(/[\s,;]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      const result = await growerInvoiceAPI.sendEmail(invoice.id, extras);
      const to = (result.recipients || []).join(', ');
      setEmailMessage(`Sent to: ${to}`);
      setExtraEmail('');
    } catch (err) {
      console.error('Failed to send invoice email:', err);
      const msg = err.response?.data?.error || 'Failed to send email.';
      setEmailMessage(msg);
    } finally {
      setEmailSending(false);
    }
  };

  const handleSaveInvoice = async () => {
    if (!generatedGrower || generatedGrower === 'all') {
      setInvoiceMessage('Select a specific grower before saving the invoice.');
      return;
    }
    try {
      setInvoiceSaving(true);
      setInvoiceMessage('');
      const payload = {
        grower: generatedGrower,
        growerName: invoice.grower_name || null,
        startDate,
        endDate,
        pickupDate: invoice.pickup_date || null,
        packDate: invoice.pack_date || null,
        bins: invoice.bins === '' ? null : parseInt(invoice.bins, 10),
        originalGrowerInvoiceNumber:
          invoice.original_grower_invoice_number || null,
        totalQty,
      };
      const saved = invoice.id
        ? await growerInvoiceAPI.update(invoice.id, payload)
        : await growerInvoiceAPI.create(payload);
      const saveInv = saved.invoice;
      setInvoice({
        id: saveInv.id,
        invoice_number: saveInv.invoice_number || '',
        grower_name: saveInv.grower_name || '',
        pickup_date: saveInv.pickup_date
          ? saveInv.pickup_date.slice(0, 10)
          : '',
        pack_date: saveInv.pack_date ? saveInv.pack_date.slice(0, 10) : '',
        bins: saveInv.bins ?? '',
        original_grower_invoice_number:
          saveInv.original_grower_invoice_number || '',
      });
      setInvoiceMessage('Invoice saved.');
    } catch (err) {
      console.error('Failed to save invoice:', err);
      setInvoiceMessage('Failed to save invoice.');
    } finally {
      setInvoiceSaving(false);
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const getReportTitle = () => {
    if (reportType === 'grower_size') {
      const growerName = generatedGrower === 'all' ? 'All Growers' : generatedGrower;
      return `Grower Report (${growerName})`;
    }
    if (reportType === 'grower_pivot') {
      return 'Grower Pivot Report';
    }
    if (reportType === 'sheet_summary') {
      return 'Pallet Sheet Summary';
    }
    return reportTypes.find(t => t.value === reportType)?.label || 'Report';
  };

  const renderReportTable = () => {
    if (reportData.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          No data available for the selected date range.
        </div>
      );
    }

    switch (reportType) {
      case 'customer':
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Customer</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Sheets</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Entries</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Total Qty</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>{row.customer_name || 'N/A'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(row.sheet_count)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(row.total_entries)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{formatNumber(row.total_qty)}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: '700' }}>
                <td style={{ padding: '12px 16px', fontSize: '14px' }}>TOTAL</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.sheet_count || 0), 0))}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.total_entries || 0), 0))}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.total_qty || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        );

      case 'grower':
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Grower</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Produce Type</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Unit</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Class</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Size</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Colour</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', fontSize: '13px' }}>Total Qty</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px', fontSize: '13px', fontWeight: '600' }}>{row.grower || '-'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.produce_type || '-'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.unit || '-'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.class_grade || '-'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.size || '-'}</td>
                  <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.colour || '-'}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>{formatNumber(row.total_qty)}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: '700' }}>
                <td colSpan="6" style={{ padding: '12px 8px', fontSize: '14px' }}>TOTAL</td>
                <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.total_qty || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        );

      case 'grower_size':
        // Calculate summary by grower/produce type (like pallet sheet view)
        const growerProduceSummary = {};
        reportData.forEach(row => {
          const grower = row.grower || 'Unknown';
          const pt = row.produce_type || 'Unknown';
          const key = `${grower}|${pt}`;
          if (!growerProduceSummary[key]) {
            growerProduceSummary[key] = { grower, produce_type: pt, total_qty: 0, entry_count: 0 };
          }
          growerProduceSummary[key].total_qty += parseInt(row.total_qty || 0);
          growerProduceSummary[key].entry_count += parseInt(row.total_entries || 1);
        });
        const growerProduceSummaryArray = Object.values(growerProduceSummary)
          .sort((a, b) => {
            if (a.grower !== b.grower) return a.grower.localeCompare(b.grower);
            return a.produce_type.localeCompare(b.produce_type);
          });
        const growerSizeTotal = reportData.reduce((sum, row) => sum + parseInt(row.total_qty || 0), 0);
        const growerSizeTotalEntries = Object.values(growerProduceSummary).reduce((sum, item) => sum + item.entry_count, 0);

        const showInvoiceHeader =
          generatedGrower && generatedGrower !== 'all' &&
          (invoice.invoice_number || invoice.grower_name ||
           invoice.pickup_date || invoice.pack_date ||
           invoice.bins !== '' || invoice.original_grower_invoice_number);

        return (
          <>
            {showInvoiceHeader && (
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '12px 16px',
                marginBottom: '16px',
                backgroundColor: '#fafafa',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: '10px 24px',
                fontSize: '13px'
              }}>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Invoice #</div>
                  <div style={{ fontWeight: '600' }}>{invoice.invoice_number || '-'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Grower Name</div>
                  <div style={{ fontWeight: '600' }}>
                    {invoice.grower_name
                      ? `${generatedGrower} - ${invoice.grower_name}`
                      : generatedGrower}
                  </div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Original Grower Invoice #(s)</div>
                  <div style={{ fontWeight: '600', whiteSpace: 'pre-line' }}>
                    {invoice.original_grower_invoice_number || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pickup Date</div>
                  <div style={{ fontWeight: '600' }}>{invoice.pickup_date ? formatDate(invoice.pickup_date) : '-'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pack Date</div>
                  <div style={{ fontWeight: '600' }}>{invoice.pack_date ? formatDate(invoice.pack_date) : '-'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bins</div>
                  <div style={{ fontWeight: '600' }}>{invoice.bins !== '' && invoice.bins != null ? invoice.bins : '-'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bin Avg</div>
                  <div style={{ fontWeight: '600' }}>{binAvg ?? '-'}</div>
                </div>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Grower</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Produce Type</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Unit</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Class</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Size</th>
                  <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', fontSize: '13px' }}>Total Qty</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px 8px', fontSize: '13px', fontWeight: '600' }}>{row.grower || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.produce_type || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.unit || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.class_grade || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.size || '-'}</td>
                    <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>{formatNumber(row.total_qty)}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: '700' }}>
                  <td colSpan="5" style={{ padding: '12px 8px', fontSize: '14px' }}>TOTAL</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '14px' }}>
                    {formatNumber(growerSizeTotal)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Summary by Grower / Produce Type */}
            <div style={{ marginTop: '30px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#374151' }}>Summary by Grower / Produce Type</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                {growerProduceSummaryArray.map((item, index) => (
                  <div key={index} style={{
                    padding: '16px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                      <strong>{item.grower}</strong> - {item.produce_type}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>{formatNumber(item.total_qty)}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      {item.entry_count} entries
                    </div>
                  </div>
                ))}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '8px',
                  border: '2px solid #10b981'
                }}>
                  <div style={{ fontSize: '14px', color: '#065f46', fontWeight: '600', marginBottom: '4px' }}>TOTAL</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#065f46' }}>{formatNumber(growerSizeTotal)}</div>
                  <div style={{ fontSize: '12px', color: '#047857', marginTop: '4px' }}>
                    {growerSizeTotalEntries} entries
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      case 'sheet_summary':
        const sheetTotal = reportData.reduce((sum, row) => sum + (row.total_qty || 0), 0);
        const sheetTotalEntries = reportData.reduce((sum, row) => sum + (row.entry_count || 0), 0);

        return (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Sheet #</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Date</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Customer</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>Produce Summary</th>
                  <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', fontSize: '13px' }}>Entries</th>
                  <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', fontSize: '13px' }}>Total Qty</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px 8px', fontSize: '13px', fontWeight: '600', color: '#10b981' }}>#{row.sheet_id}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{formatDate(row.sheet_date)}</td>
                    <td style={{ padding: '12px 8px', fontSize: '13px' }}>{row.customer_name || '-'}</td>
                    <td style={{ padding: '12px 8px', fontSize: '12px', color: '#6b7280' }}>
                      {row.produce_breakdown && row.produce_breakdown.length > 0
                        ? row.produce_breakdown.map(p => `${p.produce_type}: ${formatNumber(p.qty)}`).join(', ')
                        : '-'}
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px' }}>{formatNumber(row.entry_count)}</td>
                    <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>{formatNumber(row.total_qty)}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: '700' }}>
                  <td colSpan="4" style={{ padding: '12px 8px', fontSize: '14px' }}>TOTAL ({reportData.length} sheets)</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(sheetTotalEntries)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(sheetTotal)}</td>
                </tr>
              </tbody>
            </table>

            {/* Summary cards by produce type across all sheets */}
            {reportData.length > 0 && (
              <div style={{ marginTop: '30px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
                <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#374151' }}>Summary by Produce Type</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {(() => {
                    // Aggregate produce breakdown across all sheets
                    const produceMap = {};
                    reportData.forEach(sheet => {
                      if (sheet.produce_breakdown) {
                        sheet.produce_breakdown.forEach(p => {
                          if (!produceMap[p.produce_type]) {
                            produceMap[p.produce_type] = { qty: 0, sheet_count: 0 };
                          }
                          produceMap[p.produce_type].qty += p.qty;
                          produceMap[p.produce_type].sheet_count++;
                        });
                      }
                    });

                    const produceArray = Object.entries(produceMap)
                      .map(([type, data]) => ({ produce_type: type, ...data }))
                      .sort((a, b) => b.qty - a.qty);

                    return (
                      <>
                        {produceArray.map((item, index) => (
                          <div key={index} style={{
                            padding: '16px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                          }}>
                            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                              <strong>{item.produce_type}</strong>
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>{formatNumber(item.qty)}</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                              {item.sheet_count} sheets
                            </div>
                          </div>
                        ))}
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#d1fae5',
                          borderRadius: '8px',
                          border: '2px solid #10b981'
                        }}>
                          <div style={{ fontSize: '14px', color: '#065f46', fontWeight: '600', marginBottom: '4px' }}>TOTAL</div>
                          <div style={{ fontSize: '24px', fontWeight: '700', color: '#065f46' }}>{formatNumber(sheetTotal)}</div>
                          <div style={{ fontSize: '12px', color: '#047857', marginTop: '4px' }}>
                            {reportData.length} sheets, {sheetTotalEntries} entries
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </>
        );

      case 'date':
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Date</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Sheets</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Entries</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Total Qty</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>{formatDate(row.sheet_date)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(row.sheet_count)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(row.total_entries)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{formatNumber(row.total_qty)}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: '700' }}>
                <td style={{ padding: '12px 16px', fontSize: '14px' }}>TOTAL</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.sheet_count || 0), 0))}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.total_entries || 0), 0))}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.total_qty || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        );

      case 'produce':
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Produce Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Entries</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Total Qty</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Sheets</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Growers</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600' }}>{row.produce_type}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(row.total_entries)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{formatNumber(row.total_qty)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>{formatNumber(row.sheet_count)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>{formatNumber(row.grower_count)}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: '700' }}>
                <td style={{ padding: '12px 16px', fontSize: '14px' }}>TOTAL</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.total_entries || 0), 0))}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.total_qty || 0), 0))}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px' }}>
                  {formatNumber(reportData.reduce((sum, row) => sum + parseInt(row.sheet_count || 0), 0))}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        );

      case 'grower_pivot':
        // Only render if we have proper pivot data
        if (!reportData.length || !reportData[0].grower_qty) {
          return (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              No pivot data available for the selected date range.
            </div>
          );
        }

        // Calculate column totals for each grower
        const growerTotals = {};
        pivotGrowers.forEach(g => growerTotals[g] = 0);
        reportData.forEach(row => {
          pivotGrowers.forEach(g => {
            growerTotals[g] += (row.grower_qty && row.grower_qty[g]) || 0;
          });
        });
        const grandTotal = reportData.reduce((sum, row) => sum + (row.total || 0), 0);

        return (
          <table className="pivot-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '6px 4px', textAlign: 'left', fontWeight: '600' }}>Produce</th>
                <th style={{ padding: '6px 4px', textAlign: 'left', fontWeight: '600' }}>Size</th>
                <th style={{ padding: '6px 4px', textAlign: 'left', fontWeight: '600' }}>Col</th>
                {pivotGrowers.map(grower => (
                  <th key={grower} style={{ padding: '6px 4px', textAlign: 'right', fontWeight: '600', minWidth: '45px' }}>
                    {grower}
                  </th>
                ))}
                <th style={{ padding: '6px 4px', textAlign: 'right', fontWeight: '700', backgroundColor: '#f0fdf4' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '6px 4px', fontWeight: '600' }}>{row.produce_type || '-'}</td>
                  <td style={{ padding: '6px 4px' }}>{row.size || '-'}</td>
                  <td style={{ padding: '6px 4px' }}>{row.colour || '-'}</td>
                  {pivotGrowers.map(grower => (
                    <td key={grower} style={{ padding: '6px 4px', textAlign: 'right' }}>
                      {row.grower_qty && row.grower_qty[grower] ? formatNumber(row.grower_qty[grower]) : ''}
                    </td>
                  ))}
                  <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: '700', backgroundColor: '#f0fdf4' }}>
                    {formatNumber(row.total || 0)}
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb', fontWeight: '700' }}>
                <td colSpan="3" style={{ padding: '6px 4px' }}>TOTAL</td>
                {pivotGrowers.map(grower => (
                  <td key={grower} style={{ padding: '6px 4px', textAlign: 'right' }}>
                    {formatNumber(growerTotals[grower])}
                  </td>
                ))}
                <td style={{ padding: '6px 4px', textAlign: 'right', backgroundColor: '#dcfce7' }}>
                  {formatNumber(grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          @page {
            margin: 0.5in;
            ${reportType === 'grower_pivot' ? 'size: landscape;' : ''}
          }
          ${reportType === 'grower_pivot' ? `
          .pivot-table {
            font-size: 10px !important;
          }
          .pivot-table th, .pivot-table td {
            padding: 4px 3px !important;
          }
          ` : ''}
        }
      `}</style>

      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        {/* Header - No Print */}
        <div className="no-print" style={{
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
              <nav style={{ display: 'flex', gap: '20px' }}>
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate('/pallets')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Pallet Sheets
                </button>
                <button
                  onClick={() => navigate('/reports')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#10b981',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Reports
                </button>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => navigate('/admin')}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '15px',
                      color: '#374151',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Admin
                  </button>
                )}
              </nav>
            </div>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
              </div>
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
          <h2 style={{ marginBottom: '30px' }}>Reports</h2>

          {error && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              color: '#991b1b',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          {/* Filters - No Print */}
          <div className="no-print" style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Report Type
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  {reportTypes.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  min="2025-01-01"
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  min="2025-01-01"
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {reportType === 'grower_size' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Filter by Grower
                </label>
                <select
                  value={selectedGrower}
                  onChange={(e) => setSelectedGrower(e.target.value)}
                  style={{
                    maxWidth: '300px',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">All Growers</option>
                  {growers.map((grower, index) => (
                    <option key={index} value={grower}>{grower}</option>
                  ))}
                </select>
              </div>
            )}

            {reportType === 'grower_pivot' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Filter by Produce Type
                </label>
                <select
                  value={selectedProduceType}
                  onChange={(e) => setSelectedProduceType(e.target.value)}
                  style={{
                    maxWidth: '300px',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">All Produce Types</option>
                  {produceTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            )}

            {reportType === 'date' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Group By
                </label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  style={{
                    maxWidth: '300px',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={fetchReport}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: loading ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
              {reportData.length > 0 && (
                <button
                  onClick={handlePrint}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                >
                  Print / Save PDF
                </button>
              )}
            </div>
          </div>

          {/* Invoice Details - only for Grower Report with a specific grower */}
          {reportType === 'grower_size' &&
           generatedGrower &&
           generatedGrower !== 'all' &&
           reportData.length > 0 && (
            <div className="no-print" style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>Invoice Details</h3>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  Invoice # <strong style={{ color: '#111827' }}>{invoice.invoice_number || '—'}</strong>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Grower Name
                  </label>
                  <input
                    type="text"
                    value={invoice.grower_name}
                    onChange={(e) => handleInvoiceChange('grower_name', e.target.value)}
                    placeholder="e.g. Smith Farms"
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Original Grower Invoice #(s)
                    <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '8px' }}>
                      — one per line, one per grower delivery
                    </span>
                  </label>
                  <textarea
                    value={invoice.original_grower_invoice_number}
                    onChange={(e) => handleInvoiceChange('original_grower_invoice_number', e.target.value)}
                    placeholder={'e.g.\nSF-00123\nSF-00124'}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Pickup Date
                  </label>
                  <input
                    type="date"
                    value={invoice.pickup_date}
                    onChange={(e) => handleInvoiceChange('pickup_date', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Pack Date
                  </label>
                  <input
                    type="date"
                    value={invoice.pack_date}
                    onChange={(e) => handleInvoiceChange('pack_date', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Bins
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={invoice.bins}
                    onChange={(e) => handleInvoiceChange('bins', e.target.value)}
                    placeholder="Total bin count"
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Bin Avg (auto)
                  </label>
                  <div style={{
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: '#f9fafb',
                    color: '#374151'
                  }}>
                    {binAvg !== null ? `${binAvg} / bin` : '—'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleSaveInvoice}
                  disabled={invoiceSaving}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: invoiceSaving ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: invoiceSaving ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {invoiceSaving ? 'Saving...' : (invoice.id ? 'Update Invoice' : 'Save Invoice')}
                </button>
                {invoiceMessage && (
                  <div style={{ fontSize: '13px', color: invoiceMessage.includes('Failed') ? '#991b1b' : '#065f46' }}>
                    {invoiceMessage}
                  </div>
                )}
              </div>

              {/* Email to Grower */}
              <div style={{
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div style={{ fontSize: '13px', color: '#6b7280', marginRight: '8px' }}>
                  Email the report PDF to the grower (set the email in Growers admin)
                  {extraEmail || ' — optional extra recipients:'}
                </div>
                <input
                  type="text"
                  value={extraEmail}
                  onChange={(e) => setExtraEmail(e.target.value)}
                  placeholder="extra@example.com, other@..."
                  style={{
                    flex: '1 1 240px',
                    padding: '8px 10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
                <button
                  onClick={handleEmailInvoice}
                  disabled={emailSending || !invoice.id}
                  title={!invoice.id ? 'Save the invoice first' : 'Email the grower'}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: !invoice.id ? '#d1d5db' : (emailSending ? '#9ca3af' : '#3b82f6'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (emailSending || !invoice.id) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {emailSending ? 'Sending...' : 'Email to Grower'}
                </button>
                {emailMessage && (
                  <div style={{
                    fontSize: '13px',
                    color: emailMessage.startsWith('Sent') ? '#065f46' : '#991b1b',
                    width: '100%'
                  }}>
                    {emailMessage}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Report Results */}
          {!loading && startDate && endDate && reportData !== null && reportData.length === 0 && (
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#374151' }}>No Data Found</h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                No pallet sheets found for the selected date range ({formatDate(startDate)} to {formatDate(endDate)}).
                <br />
                Try selecting a different date range or create some pallet sheets first.
              </p>
            </div>
          )}

          {reportData.length > 0 && (
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ marginBottom: '20px', borderBottom: '2px solid #10b981', paddingBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>
                  {getReportTitle()}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  {formatDate(startDate)} to {formatDate(endDate)}
                </p>
              </div>
              <div style={{ overflowX: 'auto' }}>
                {renderReportTable()}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Reports;
